import React, { useState } from "react"
import { useIntl } from "react-intl"
import { Panel } from "@walmart-web/livingdesign-components"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { FilterLabel, Button, PanelButtons } from "./components"
import { Tuple } from "components/Shared/Interfaces"
import { default as ButtonLD } from "components/Shared/Button"
import "./index.scss"

export interface ButtonProps {
	title: string
	toOpen: string
	dependencies: Array<string>
}

export interface MenuProps {
	panelContent: Record<string, JSX.Element>
	buttons: Array<ButtonProps>
}

const Menu: React.FC<MenuProps> = ({ panelContent, buttons }) => {
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const [content, setContent] = useState<string>("")
	const [selectedFilters, setSelectedFilters] = useState<
		Record<string, Array<Tuple>>
	>({})
	const { filters, setFilters, clearFilters } = useFiltersContext()
	const intl = useIntl()

	const handleOnClick = (toOpen: string) => {
		setContent(toOpen)
		setIsOpen(true)
	}

	const handleFiltersChange = (key: string, filters: Array<Tuple>) => {
		setSelectedFilters({ ...selectedFilters, [key]: filters })
	}

	const onClickApply = () => {
		setFilters({ ...filters, ...selectedFilters })
		setIsOpen(false)
	}

	const onClickClear = () => {
		setSelectedFilters({})
		clearFilters()
	}

	const renderTitleButton = (
		title: string,
		dependencies: Array<string>
	): string => {
		let counter = 0
		dependencies.forEach((item) => (counter += filters[item].length))
		return counter ? `${title} (${counter})` : title
	}

	return (
		<div className="filters-menu">
			<FilterLabel />
			<div className="filters-menu-button-container">
				{buttons.map((content) => (
					<Button
						key={`button-${content.title}`}
						title={renderTitleButton(content.title, content.dependencies)}
						onClick={() => handleOnClick(content.toOpen)}
					/>
				))}
			</div>
			<ButtonLD
				onClick={() => onClickClear()}
				variant="tertiary"
				UNSAFE_className="filters-clean-button"
			>
				{intl.formatMessage({ id: "phronesis.filters.clean" })}
			</ButtonLD>
			<Panel
				title={intl.formatMessage(
					{ id: "phronesis.filters.panel.title" },
					{ filterId: content }
				)}
				actions={
					<PanelButtons
						cancelTitle={intl.formatMessage({ id: "phronesis.buttons.cancel" })}
						applyTitle={intl.formatMessage({ id: "phronesis.filters.apply" })}
						onClickCancel={() => setIsOpen(false)}
						onClickApply={() => onClickApply()}
					/>
				}
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				UNSAFE_className="filters-menu-panel"
				position="right"
				size="medium"
			>
				{panelContent[content] &&
					React.cloneElement(panelContent[content], {
						setStoreFilters: handleFiltersChange,
					})}
			</Panel>
		</div>
	)
}

export default Menu
